import { useState, type JSX } from 'react'
import { Column } from 'primereact/column'
import { Eye, Pencil, RefreshCw, Power, Trash2, Link2 } from 'lucide-react'
import DataTable from '../../ui/DataTable.js'
import DisconnectConfirmDialog from './DisconnectConfirmDialog.js'
import DeleteAccountConfirmDialog from './DeleteAccountConfirmDialog.js'
import { useToast } from '../../system/ToastProvider.js'
import { PROVIDER_ICONS } from '../../../pages/admin/channel-provider-meta.js'
import { formatDateTime } from '../../../utils/formatDate.js'
import type { ConnectedAccount, ChannelProviderName } from '../../../pages/admin/channels-types.js'

function isKnownProvider(provider: string): provider is ChannelProviderName {
  return provider in PROVIDER_ICONS
}

export interface AccountsListTableProps {
  accounts: ConnectedAccount[]
  loading: boolean
  onView?: (account: ConnectedAccount) => void
  onEdit?: (account: ConnectedAccount) => void
  onReconnect?: (account: ConnectedAccount) => void
  onDisconnect: (account: ConnectedAccount) => Promise<void>
  onDelete?: (account: ConnectedAccount) => Promise<void>
}

const STATUS_STYLES: Record<ConnectedAccount['status'], string> = {
  connected: 'bg-green-50 text-green-700 border border-green-200',
  expired: 'bg-amber-50 text-amber-700 border border-amber-200',
  revoked: 'bg-gray-100 text-gray-500 border border-gray-200',
}

function StatusBadge({ status }: { status: ConnectedAccount['status'] }): JSX.Element {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize ${STATUS_STYLES[status]}`}>
      {status}
    </span>
  )
}

export default function AccountsListTable(props: AccountsListTableProps): JSX.Element {
  const toast = useToast()
  const [pendingDisconnect, setPendingDisconnect] = useState<ConnectedAccount | null>(null)
  const [disconnecting, setDisconnecting] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<ConnectedAccount | null>(null)
  const [deleting, setDeleting] = useState(false)

  async function handleConfirmDisconnect(): Promise<void> {
    if (!pendingDisconnect) return
    setDisconnecting(true)
    try {
      await props.onDisconnect(pendingDisconnect)
      setPendingDisconnect(null)
      toast.success('Account disconnected')
    } catch (e) {
      toast.error('Failed to disconnect account', e instanceof Error ? e.message : 'Failed to disconnect account.')
    } finally {
      setDisconnecting(false)
    }
  }

  async function handleConfirmDelete(): Promise<void> {
    if (!pendingDelete || !props.onDelete) return
    setDeleting(true)
    try {
      await props.onDelete(pendingDelete)
      setPendingDelete(null)
      toast.success('Account deleted')
    } catch (e) {
      toast.error('Failed to delete account', e instanceof Error ? e.message : 'Failed to delete account.')
    } finally {
      setDeleting(false)
    }
  }

  const actionBodyTemplate = (row: ConnectedAccount) => (
    <div className="flex items-center gap-1" style={{ flexWrap: 'nowrap' }}>
      {props.onView && (
        <button
          onClick={() => props.onView?.(row)}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-[#3454cf] transition-all shrink-0"
          title="View"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
      )}
      {props.onEdit && (
        <button
          onClick={() => props.onEdit?.(row)}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-[#3454cf] transition-all shrink-0"
          title="Edit"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
      {props.onReconnect && row.status !== 'connected' && (
        <button
          onClick={() => props.onReconnect?.(row)}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-blue-600 transition-all shrink-0"
          title="Reconnect"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      )}
      {row.status === 'connected' && (
        <button
          onClick={() => setPendingDisconnect(row)}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-amber-500 transition-all shrink-0"
          title="Disconnect"
        >
          <Power className="h-3.5 w-3.5" />
        </button>
      )}
      {props.onDelete && (
        <button
          onClick={() => setPendingDelete(row)}
          disabled={row.status !== 'revoked'}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-red-500 transition-all shrink-0 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400"
          title={row.status === 'revoked' ? 'Delete' : 'Disconnect the account before deleting it'}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )

  return (
    <>
      <DataTable
        value={props.accounts}
        loading={props.loading}
        emptyMessage="No accounts connected yet."
        className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
      >
        <Column
          header="Account Name"
          body={(row: ConnectedAccount) => (
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
                {isKnownProvider(row.provider) ? PROVIDER_ICONS[row.provider] : <Link2 className="h-5 w-5 text-[#3454cf]" />}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{row.displayName}</p>
                <p className="text-xs text-gray-400">{row.accountKey}</p>
              </div>
            </div>
          )}
        />
        <Column
          header="Provider"
          body={(row: ConnectedAccount) => (
            <span className="text-sm font-medium text-gray-700 capitalize">{row.provider}</span>
          )}
        />
        <Column header="Status" body={(row: ConnectedAccount) => <StatusBadge status={row.status} />} />
        <Column
          header="Auth Type"
          body={(row: ConnectedAccount) => (
            <span className="text-sm text-gray-700 capitalize">{row.authType}</span>
          )}
        />
        <Column
          header="Created Date"
          body={(row: ConnectedAccount) => (
            <span className="text-sm text-gray-700">{formatDateTime(row.createdAt)}</span>
          )}
        />
        <Column body={actionBodyTemplate} header="Actions" style={{ width: '160px' }} />
      </DataTable>

      <DisconnectConfirmDialog
        visible={pendingDisconnect !== null}
        displayName={pendingDisconnect?.displayName ?? ''}
        pending={disconnecting}
        onCancel={() => setPendingDisconnect(null)}
        onConfirm={() => void handleConfirmDisconnect()}
      />

      <DeleteAccountConfirmDialog
        visible={pendingDelete !== null}
        displayName={pendingDelete?.displayName ?? ''}
        pending={deleting}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => void handleConfirmDelete()}
      />
    </>
  )
}
